import {
  CalendarCheck, ClipboardList, Wallet, Users, FileBarChart, LucideIcon,
} from 'lucide-react';

export interface SolutionFaq {
  q: string;
  a: string;
}

export interface Solution {
  slug: string;
  icon: LucideIcon;
  title: string;
  tagline: string;
  description: string;
  benefits: string[];
  idealFor: string[];
  faq: SolutionFaq[];
  // Caminhos pro print real da solução, um pra cada tema (ex.
  // '/assets/images/screenshots/agendalight.png' / 'agendadark.png') — a
  // página escolhe a variante certa conforme o tema ativo. Enquanto não
  // existem, a página usa um card ilustrativo com o ícone no lugar.
  imageLight?: string;
  imageDark?: string;
}

export const SOLUTIONS: Solution[] = [
  {
    slug: 'agenda',
    icon: CalendarCheck,
    title: 'Agenda',
    tagline: 'Sua clínica no horário certo, sempre.',
    description:
      'Agendamentos organizados por profissional, bloqueios de horário e confirmação automática, tudo em um calendário único e fácil de usar.',
    benefits: [
      'Visão por dia, semana ou mês, filtrando por profissional ou serviço',
      'Bloqueios de horário por folga, feriado ou evento da clínica',
      'Detecção automática de conflito de horário na hora de agendar',
      'Agendamentos recorrentes pra pacientes em acompanhamento contínuo',
    ],
    idealFor: [
      'Clínicas com mais de um profissional atendendo',
      'Quem perde tempo remarcando horário por telefone',
      'Consultórios que precisam de confirmação automática',
      'Equipes que dividem a agenda entre recepção e profissionais',
    ],
    faq: [
      {
        q: 'Dá pra ter uma agenda por profissional?',
        a: 'Sim, cada profissional tem sua própria agenda, com visão individual ou consolidada da clínica inteira.',
      },
      {
        q: 'O paciente recebe confirmação do agendamento?',
        a: 'Sim, a confirmação e os lembretes acontecem automaticamente, sem depender da recepção lembrar de avisar.',
      },
      {
        q: 'Consigo bloquear horários de folga ou feriado?',
        a: 'Sim, bloqueios de horário são configuráveis por profissional ou pra clínica inteira.',
      },
    ],
    imageLight: '/assets/images/screenshots/agendalight.png',
    imageDark: '/assets/images/screenshots/agendadark.png',
  },
  {
    slug: 'prontuario',
    icon: ClipboardList,
    title: 'Prontuário',
    tagline: 'Histórico clínico completo, sempre à mão.',
    description:
      'Evoluções clínicas registradas por sessão, com histórico completo por paciente e modelos prontos pra agilizar o dia a dia.',
    benefits: [
      'Evoluções por sessão, vinculadas ao paciente e ao profissional',
      'Modelos de evolução prontos, personalizáveis por especialidade',
      'Histórico completo acessível a qualquer momento',
      'Dados isolados e seguros, visíveis só pra quem tem permissão',
    ],
    idealFor: [
      'Clínicas que ainda registram evolução em papel ou planilha',
      'Profissionais que precisam consultar histórico rápido em consulta',
      'Equipes com mais de uma especialidade atendendo o mesmo paciente',
      'Quem precisa de padronização no registro clínico',
    ],
    faq: [
      {
        q: 'Cada profissional só vê os próprios registros?',
        a: 'O acesso é controlado por perfil — profissionais veem o que é relevante pro atendimento deles, respeitando a hierarquia da clínica.',
      },
      {
        q: 'Dá pra personalizar o modelo de evolução?',
        a: 'Sim, cada clínica pode ter modelos próprios de evolução, adaptados à especialidade que atende.',
      },
      {
        q: 'O histórico do paciente fica todo em um só lugar?',
        a: 'Sim, todas as evoluções do paciente ficam organizadas cronologicamente em um único prontuário digital.',
      },
    ],
    imageLight: '/assets/images/screenshots/prontuariolight.png',
    imageDark: '/assets/images/screenshots/prontuariodark.png',
  },
  {
    slug: 'financeiro',
    icon: Wallet,
    title: 'Financeiro',
    tagline: 'Fluxo de caixa sob controle, sem planilha.',
    description:
      'Pagamentos, parcelamentos e despesas em um só lugar, com visão clara de quanto entra e quanto sai da clínica.',
    benefits: [
      'Pagamentos vinculados a atendimentos, com status de quitação',
      'Parcelamento automático com controle de vencimento',
      'Despesas da clínica organizadas por categoria',
      'Projeção de fluxo de caixa baseada na agenda',
    ],
    idealFor: [
      'Clínicas que ainda controlam o financeiro em planilha',
      'Quem precisa saber rápido quanto tem a receber no mês',
      'Clínicas com repasse de comissão pra profissionais',
      'Gestores que querem visão financeira sem depender de contador pra tudo',
    ],
    faq: [
      {
        q: 'Consigo ver quanto cada profissional tem a receber?',
        a: 'Sim, o repasse por comissão é calculado automaticamente por profissional, com base nos atendimentos realizados.',
      },
      {
        q: 'Dá pra parcelar um atendimento?',
        a: 'Sim, o parcelamento é automático e o sistema controla o vencimento de cada parcela.',
      },
      {
        q: 'Preciso lançar as despesas manualmente?',
        a: 'Sim, as despesas são lançadas e categorizadas pela clínica, e entram na visão geral de fluxo de caixa.',
      },
    ],
    imageLight: '/assets/images/screenshots/financeirolight.png',
    imageDark: '/assets/images/screenshots/financeirodark.png',
  },
  {
    slug: 'equipe',
    icon: Users,
    title: 'Gestão de Equipe',
    tagline: 'Cada pessoa com o acesso certo.',
    description:
      'Profissionais, secretárias e administradores com permissões específicas pro que cada um precisa fazer no sistema.',
    benefits: [
      'Perfis de acesso por cargo: admin, secretária e profissional',
      'Comissionamento configurável por profissional',
      'Hierarquia de administradores com controle sobre quem edita quem',
      'Registro de auditoria de ações importantes na clínica',
    ],
    idealFor: [
      'Clínicas com equipe de recepção separada dos profissionais',
      'Quem precisa restringir acesso a dados financeiros ou clínicos',
      'Clínicas em crescimento, contratando novos profissionais',
      'Gestores que querem rastrear quem fez o quê no sistema',
    ],
    faq: [
      {
        q: 'A secretária consegue ver o financeiro?',
        a: 'Só se a clínica der essa permissão — cada perfil de acesso define exatamente o que a pessoa pode ver e fazer.',
      },
      {
        q: 'Dá pra ter mais de um administrador?',
        a: 'Sim, e o dono da clínica mantém controle sobre quem os outros administradores podem editar.',
      },
      {
        q: 'Fica registrado quem alterou o quê?',
        a: 'Sim, ações importantes ficam registradas em um log de auditoria acessível pela administração.',
      },
    ],
    imageLight: '/assets/images/screenshots/usuarioslight.png',
    imageDark: '/assets/images/screenshots/usuariosdark.png',
  },
  {
    slug: 'faturamento-tiss',
    icon: FileBarChart,
    title: 'Faturamento TISS',
    tagline: 'Guias e lotes prontos pro convênio.',
    description:
      'Geração de guias TISS e lotes de faturamento direto do atendimento, sem retrabalho manual pra fechar com o convênio.',
    benefits: [
      'Guias TISS geradas a partir do próprio atendimento',
      'Lotes organizados por convênio, prontos pra envio',
      'Menos erro de digitação, menos glosa',
      'Histórico de faturamento por convênio',
    ],
    idealFor: [
      'Clínicas que atendem por convênio, não só particular',
      'Quem perde tempo preenchendo guia TISS manualmente',
      'Clínicas com alto índice de glosa por erro de preenchimento',
      'Equipes que fecham lote de faturamento todo mês',
    ],
    faq: [
      {
        q: 'Preciso preencher a guia TISS na mão?',
        a: 'Não, a guia é gerada automaticamente a partir dos dados do próprio atendimento já registrado no sistema.',
      },
      {
        q: 'Dá pra organizar por convênio?',
        a: 'Sim, os lotes de faturamento são organizados por convênio, prontos pra conferência e envio.',
      },
      {
        q: 'Isso reduz glosa?',
        a: 'Reduz bastante o erro de digitação manual, que é uma das principais causas de glosa em faturamento TISS.',
      },
    ],
    imageLight: '/assets/images/screenshots/tisslight.png',
    imageDark: '/assets/images/screenshots/tissdark.png',
  },
];
